/**
 * Editing a customer's details after the record exists (tv-pzvy, approved 6 Oct).
 *
 * The ONE set of rules the record's "Edit details" pop-up and the server's
 * update-customer-details intent share, so the screen and the server can never
 * disagree on what a valid edit is:
 *  - values are trimmed; the IG handle is stored as `@handle`, the state in capitals;
 *  - a first name is required;
 *  - the mobile must be an Australian number WHEN IT CHANGES (or is cleared). An
 *    unchanged number on file is kept as it is, so a legacy row whose stored phone
 *    isn't AU-format (most of the demo seed) can still have its name fixed;
 *  - email, IG handle and postcode are checked only when given and changed (what
 *    is already on file never blocks a save of another field);
 *  - the IG handle compares by its normalised username (stored "@x" or "x" is the
 *    same handle), the mobile by its digits;
 *  - a duplicate mobile is NOT a problem here: the pop-up warns, saving is allowed.
 */
import { isAuPhone } from './contact';
import { isValidInstagramHandle, normalizeInstagramHandle } from './instagram';

/** Every staff persona may edit a customer's details (artists included). */
export const STAFF_PERSONAS: ReadonlySet<string> = new Set([
  'georgina',
  'messenger',
  'studio-manager',
  'bookings-agent',
  'artist',
  'apprentice',
  'owner',
  'finance',
  'finance-manager',
  // 75n6: the Ops sub-role seats.
  'confirmations',
  'sweeper',
  'floor',
]);

/** The fields the pop-up edits, in screen order. */
export const CUSTOMER_DETAIL_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'phone',
  'email',
  'instagramHandle',
  'suburb',
  'state',
  'postcode',
] as const;

export type CustomerDetailField = (typeof CUSTOMER_DETAIL_FIELDS)[number];

/** A customer's editable details, cleaned ('' = none). */
export type CustomerDetails = Record<CustomerDetailField, string>;

/** What a caller sends: a field left out keeps what is on file; null clears it. */
export type CustomerDetailsInput = Partial<Record<CustomerDetailField, string | null | undefined>>;

/** One field the edit can't save, with the reason in plain words. */
export interface CustomerDetailsProblem {
  field: CustomerDetailField;
  code: 'first-name-required' | 'invalid-mobile' | 'invalid-email' | 'invalid-instagram' | 'invalid-postcode' | 'too-long';
  message: string;
}

/** One line of a record's details history (server-stamped). */
export interface CustomerDetailsHistoryEntry {
  id: string;
  at: string;
  /** The signed-in person id (or persona) who saved it. */
  by?: string;
  /** Their name as shown at sign-in. */
  byName?: string;
  fields: CustomerDetailField[];
  /** Old values for the name, mobile and email (only those; only when not blank). */
  was: { name?: string; mobile?: string; email?: string };
}

const MAX_LEN = 80;

/** A comparable key for a phone number: separators dropped, +61 / 61 read as 0. */
export function phoneKey(raw: string | null | undefined): string {
  return (raw ?? '').replace(/[\s\-().]/g, '').replace(/^\+?61/, '0');
}

/** A basic email shape check (something@something.tld, no spaces). */
export function isPlausibleEmail(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

function cleanField(field: CustomerDetailField, raw: string): string {
  const v = raw.trim();
  if (field === 'instagramHandle') {
    const h = normalizeInstagramHandle(v);
    return h ? `@${h}` : '';
  }
  if (field === 'state') return v.toUpperCase();
  return v;
}

/** Trim and normalise an edit. A field the caller left out keeps `onFile`'s value. */
export function cleanCustomerDetails(input: CustomerDetailsInput, onFile?: Partial<CustomerDetails>): CustomerDetails {
  const out = {} as CustomerDetails;
  for (const field of CUSTOMER_DETAIL_FIELDS) {
    const given = input[field];
    out[field] = given === undefined ? (onFile?.[field] ?? '') : cleanField(field, given ?? '');
  }
  return out;
}

/** Why a (cleaned) edit can't be saved; empty when it can. */
export function customerDetailsProblems(
  next: CustomerDetails,
  onFile: Pick<CustomerDetails, 'phone'> & Partial<CustomerDetails>,
): CustomerDetailsProblem[] {
  const changed = (f: CustomerDetailField) => !sameValue(f, onFile[f] ?? '', next[f]);
  const problems: CustomerDetailsProblem[] = [];
  for (const field of CUSTOMER_DETAIL_FIELDS) {
    if (next[field].length > MAX_LEN) {
      problems.push({ field, code: 'too-long', message: `Keep it under ${MAX_LEN} characters.` });
    }
  }
  if (!next.firstName) problems.push({ field: 'firstName', code: 'first-name-required', message: 'Add a first name.' });
  if (phoneKey(next.phone) !== phoneKey(onFile.phone) && !isAuPhone(next.phone)) {
    problems.push({ field: 'phone', code: 'invalid-mobile', message: 'Enter an Australian mobile, like 0412 555 018.' });
  }
  if (next.email && changed('email') && !isPlausibleEmail(next.email)) {
    problems.push({ field: 'email', code: 'invalid-email', message: 'That email doesn’t look right.' });
  }
  if (next.instagramHandle && changed('instagramHandle') && !isValidInstagramHandle(normalizeInstagramHandle(next.instagramHandle))) {
    problems.push({ field: 'instagramHandle', code: 'invalid-instagram', message: 'That isn’t a valid Instagram handle.' });
  }
  if (next.postcode && changed('postcode') && !/^\d{4}$/.test(next.postcode)) {
    problems.push({ field: 'postcode', code: 'invalid-postcode', message: 'Postcodes are four digits.' });
  }
  return problems;
}

function sameValue(f: CustomerDetailField, a: string, b: string): boolean {
  if (f === 'phone') return phoneKey(a) === phoneKey(b);
  if (f === 'instagramHandle') return normalizeInstagramHandle(a) === normalizeInstagramHandle(b);
  return a.trim() === b.trim();
}

/** The fields that differ (a reformatted mobile or "@x" vs "x" handle is not a change). */
export function customerDetailsChanges(before: CustomerDetails, after: CustomerDetails): CustomerDetailField[] {
  return CUSTOMER_DETAIL_FIELDS.filter((f) => !sameValue(f, before[f], after[f]));
}

// How each field reads in the history line; the address parts read as one.
const FIELD_WORD: Record<CustomerDetailField, string> = {
  firstName: 'name',
  lastName: 'name',
  preferredName: 'preferred name',
  phone: 'mobile',
  email: 'email',
  instagramHandle: 'Instagram handle',
  suburb: 'address',
  state: 'address',
  postcode: 'address',
};

function listWords(words: string[]): string {
  return words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** "Georgina updated the mobile (was 0412 555 018)": the record's history line. */
export function detailsHistorySentence(e: Pick<CustomerDetailsHistoryEntry, 'byName' | 'fields' | 'was'>): string {
  const words = [...new Set(e.fields.map((f) => FIELD_WORD[f]))];
  const olds = (
    [
      ['name', e.was.name],
      ['mobile', e.was.mobile],
      ['email', e.was.email],
    ] as const
  ).filter(([word, v]) => v && words.includes(word));
  const was =
    olds.length === 0
      ? ''
      : olds.length === 1
        ? ` (was ${olds[0]![1]})`
        : ` (${olds.map(([word, v]) => `${word} was ${v}`).join(', ')})`;
  return `${e.byName?.trim() || 'Staff'} updated the ${listWords(words)}${was}`;
}
